/**
 * MetricCard Component
 *
 * One headline number with its label and, when there is a previous period
 * to compare with, the change since then.
 *
 * Usage:
 *   <MetricCard
 *     title="Cost per result"
 *     value="$4.20"
 *     trend={-12.5}
 *     goodWhen="down"
 *     comparedTo="previous 30 days"
 *   />
 */

import React from 'react';
import { Card } from '@/components/ui/Card';
import { HintBadge } from '@/components/vibe-kit/help/HintBadge';
import { formatTrend } from '@/utils/analyticsFormat';

export type MetricAccentColor = 'blue' | 'violet' | 'emerald' | 'amber';

export interface MetricCardProps {
  title: string;
  value: string | number;
  icon?: React.ReactNode;
  /** Percent change vs the previous period; null/undefined hides the pill. */
  trend?: number | null;
  /** Which direction is good news: costs go down, results go up, spend is neither. */
  goodWhen?: 'up' | 'down' | 'neutral';
  comparedTo?: string;
  subtitle?: string;
  loading?: boolean;
  accentColor?: MetricAccentColor;
  hint?: { title: string; content: string };
}

// Accent names resolve to the central design tokens.
const accentStyles: Record<MetricAccentColor, string> = {
  blue: 'bg-primary-soft text-primary',
  violet: 'bg-info-soft text-info',
  emerald: 'bg-success-soft text-success',
  amber: 'bg-warning-soft text-warning',
};

export const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  icon,
  trend,
  goodWhen = 'up',
  comparedTo,
  subtitle,
  loading = false,
  accentColor = 'blue',
  hint,
}) => {
  if (loading) {
    return (
      <Card variant="default" padding="none" className="!rounded-card px-[18px] py-[18px]">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-surface rounded w-24" />
          <div className="h-8 bg-surface rounded w-32" />
          <div className="h-3 bg-surface rounded w-20" />
        </div>
      </Card>
    );
  }

  const trendText = formatTrend(trend);
  const flat = trend === 0 || goodWhen === 'neutral';
  const good = !flat && trend != null && (trend > 0) === (goodWhen === 'up');

  return (
    <Card variant="default" padding="none" className="!rounded-card px-[18px] py-[18px] flex flex-col">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1 text-label uppercase text-ink-3">
          {title}
          {hint && <HintBadge title={hint.title} content={hint.content} />}
        </div>
        {icon && (
          <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${accentStyles[accentColor]}`}>
            {icon}
          </div>
        )}
      </div>

      <p className="font-money text-[28px] leading-tight font-semibold tabular text-ink mt-2 break-words">
        {value}
      </p>

      {subtitle && <p className="text-xs text-ink-3 mt-1">{subtitle}</p>}

      {trendText && (
        <p
          className="mt-auto pt-3 flex items-center gap-1.5 text-[12px] text-ink-3"
          title={comparedTo ? `Compared with the ${comparedTo}` : undefined}
        >
          <span
            className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-semibold ${
              flat ? 'bg-surface text-ink-2' : good ? 'bg-success-soft text-success' : 'bg-danger-soft text-danger-text'
            }`}
          >
            {trend !== 0 && (
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d={trend! > 0 ? 'M5 15l7-7 7 7' : 'M19 9l-7 7-7-7'}
                />
              </svg>
            )}
            {trendText}
          </span>
          {comparedTo && <span className="hidden sm:inline">vs {comparedTo}</span>}
        </p>
      )}
    </Card>
  );
};

export default MetricCard;
